import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ADMIN_API, useAdminAuth } from "./AdminAuth";
import { adminToken, callbackSession, signInError } from "./adminSession";

export function AuthCallbackHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAdminAuth();
  const sessionId = useRef(callbackSession(location));
  const ran = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // Remove the temporary credential from history before calling the backend.
    navigate("/admin/auth/callback", { replace: true });
    adminToken.clear();
    if (!sessionId.current) {
      setError("No sign-in session was received. Please start again using Sign in with Google.");
      return;
    }
    async function completeSignIn() {
      try {
        const { data } = await axios.post(`${ADMIN_API}/auth/session`, {
          session_id: sessionId.current,
        }, { withCredentials: true, timeout: 30000 });
        sessionId.current = null;
        if (!data.session_token) throw new Error("No session token returned");
        adminToken.set(data.session_token);
        // Verify the actual persisted session before displaying an admin screen.
        const verified = await axios.get(`${ADMIN_API}/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${data.session_token}` },
          timeout: 20000,
        });
        setUser(verified.data.user);
        navigate("/admin", { replace: true });
      } catch (err) {
        sessionId.current = null;
        adminToken.clear();
        setUser(null);
        setError(signInError(err));
      }
    }
    completeSignIn();
  }, [navigate, setUser]);

  return (
    <div className="auth-callback" data-testid="auth-callback">
      <div className="auth-callback-card">
        <h2 data-testid="auth-callback-title">{error ? "Sign-in failed" : "Finishing sign-in…"}</h2>
        <p className={error ? "error" : "muted"} role={error ? "alert" : "status"} data-testid="auth-callback-message">
          {error || "Verifying your admin access."}
        </p>
        {error && <Link className="btn btn-primary" to="/admin/login" data-testid="auth-retry">Try again</Link>}
      </div>
    </div>
  );
}